import {
  useTranscriptExpansion,
  useTranscriptSettings,
} from "./TranscriptContext";
import { Fragment } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { type StreamMessage } from "../../hooks/useStreamingText";
import { formatHoverTime } from "../../lib/formatTimestamp";
import { userDisplayName } from "../../lib/userDisplayName";
import { parseApprovePlanInput } from "../../tools/ApprovePlan";
import { SeedBranchStorageNotice } from "../../tools/SeedBranchStorage";
import { Avatar, getUserColor } from "../Avatar";
import { ServiceAccountAvatar } from "../ServiceAccountAvatar";
import { Tooltip } from "../Tooltip";
import { AssistantMessage } from "./AssistantMessage";
import { CompactionEntry } from "./CompactionEntry";
import { ToolUse } from "./ToolUse";
import { TypewriterText } from "./TypewriterText";

import { LOADED_SKILL_PREFIX } from "../../../shared/skills/attachedSkills";
import {
  GitSyncMessage,
  isPushMessage,
  LoadedSkillNotice,
  MergeMessage,
  PushMessage,
  SystemNotice,
  systemMessageIcon,
  systemMessageText,
} from "./SystemMessages";
import { UserMessageBubble } from "./UserMessageBubble";

import { type GroupedEntry, STANDALONE_TOOLS } from "./groupChatEntries";
import {
  ACTIVITY_ROW_GAP,
  Message,
  UserMessageTimestamp,
  UserMessageWrapper,
} from "./transcriptStyles";

type Props = {
  entry: ChatEntryWithSubTables;
  nextGrouped: GroupedEntry | undefined;
  interrupted: boolean;
  dateMarker: React.ReactNode;
  activityAbove: boolean;
  activityBelow: boolean;
  streamingEntries: ReadonlyMap<string, StreamMessage>;
};

export function MessageRow({
  entry,
  nextGrouped,
  interrupted,
  dateMarker,
  activityAbove,
  activityBelow,
  streamingEntries,
}: Props) {
  const { expandedToolUse, setExpandedToolUse } = useTranscriptExpansion();
  const { isGroupChat, currentUserId, toolsByName, onProvideToolResult } =
    useTranscriptSettings();
  const nextEntry =
    nextGrouped?.type === "single" ? nextGrouped.entry : undefined;
  const isUser = entry.entryType === "UserMessage";
  const roleChange =
    nextEntry &&
    (entry.entryType === "UserMessage") !==
      (nextEntry.entryType === "UserMessage");
  const typeChange = nextEntry && entry.entryType !== nextEntry.entryType;
  const nextIsToolGroup = nextGrouped?.type === "toolGroup";
  const systemText = entry.systemMessage?.text ?? "";
  const setApart =
    !activityAbove &&
    (systemText.startsWith("Saved:") ||
      systemText.startsWith("Merged") ||
      systemText.startsWith("Synced from git") ||
      isPushMessage(systemText));

  return (
    <Fragment>
      {dateMarker}
      <Message
        data-chat-entry-id={entry.id}
        style={{
          ...(entry.entryType === "AssistantMessage" && {
            flexDirection: "column",
          }),
          ...(isUser && {
            maxWidth: "85%",
            alignSelf: "flex-end",
            alignItems: "flex-end",
            marginBottom: 32,
          }),
          ...(setApart && { marginTop: 16 }),
          ...(isUser && nextIsToolGroup && nextGrouped.isTrailing
            ? { marginBottom: 28 }
            : activityBelow
              ? { marginBottom: ACTIVITY_ROW_GAP }
              : roleChange || (isUser && nextIsToolGroup)
                ? { marginBottom: isUser ? 48 : 32 }
                : (typeChange || nextIsToolGroup) && {
                    marginBottom: 12,
                  }),
        }}
      >
        {entry.entryType === "UserMessage" &&
          entry.userMessage &&
          (() => {
            const messageUser = entry.userMessage.user;
            const messageServiceAccount = entry.userMessage.serviceAccount;
            const author = messageUser ?? messageServiceAccount;
            const userColor =
              isGroupChat && author ? getUserColor(author.id) : null;

            return (
              <UserMessageWrapper data-user-message-id={entry.id}>
                <UserMessageBubble
                  text={entry.userMessage.text ?? ""}
                  isDictated={!!entry.userMessage.isDictated}
                  attachments={entry.attachments}
                  userColor={userColor}
                />
                {isGroupChat && author && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: -6,
                      right: -6,
                    }}
                  >
                    <Tooltip
                      content={
                        messageUser
                          ? messageUser.id === currentUserId
                            ? "You"
                            : userDisplayName(messageUser)
                          : messageServiceAccount!.name
                      }
                    >
                      {messageUser ? (
                        <Avatar
                          user={messageUser}
                          size={12}
                          tooltip={false}
                          bare={false}
                        />
                      ) : (
                        <ServiceAccountAvatar
                          seed={messageServiceAccount!.id}
                          size={12}
                        />
                      )}
                    </Tooltip>
                  </div>
                )}
                <UserMessageTimestamp>
                  {formatHoverTime(entry.createdAt)}
                </UserMessageTimestamp>
              </UserMessageWrapper>
            );
          })()}

        {entry.entryType === "AssistantMessage" && (
          <AssistantMessage
            entry={entry}
            stream={streamingEntries.get(entry.id)}
          />
        )}

        {entry.entryType === "Compaction" && entry.compaction && (
          <CompactionEntry />
        )}

        {entry.entryType === "AssistantToolUse" &&
          entry.toolCall?.toolName === "approvePlan" &&
          (() => {
            const plan = parseApprovePlanInput(entry.toolCall.input);
            if (!plan) return null;
            return (
              <TypewriterText
                text={`**${plan.title}**\n\n${plan.plan}`}
                streaming={false}
              />
            );
          })()}

        {entry.entryType === "AssistantToolUse" &&
          entry.toolCall?.toolName === "seedBranchStorage" && (
            <SeedBranchStorageNotice
              entryId={entry.id}
              input={entry.toolCall.input}
              completed={entry.toolCall.completedAt != null}
              isError={entry.toolCall.isError ?? false}
              interrupted={interrupted}
            />
          )}

        {entry.entryType === "SystemMessage" &&
          entry.systemMessage &&
          (entry.systemMessage.text.startsWith(LOADED_SKILL_PREFIX) ? (
            <LoadedSkillNotice
              name={entry.systemMessage.text.slice(LOADED_SKILL_PREFIX.length)}
            />
          ) : entry.systemMessage.text.startsWith("Merged") ? (
            <MergeMessage text={entry.systemMessage.text} />
          ) : entry.systemMessage.text.startsWith("Synced from git") ? (
            <GitSyncMessage text={entry.systemMessage.text} />
          ) : isPushMessage(entry.systemMessage.text) ? (
            <PushMessage text={entry.systemMessage.text} />
          ) : (
            <SystemNotice
              icon={systemMessageIcon(entry.systemMessage.text)}
              text={systemMessageText(entry.systemMessage.text)}
            />
          ))}

        {entry.entryType === "AssistantToolUse" &&
          entry.toolCall &&
          !STANDALONE_TOOLS.has(entry.toolCall.toolName) && (
            <ToolUse
              toolUse={{
                id: entry.id,
                toolName: entry.toolCall.toolName,
                input: entry.toolCall.input,
                output: entry.toolCall.output,
                isError: entry.toolCall.isError,
              }}
              result={
                entry.toolCall.completedAt != null
                  ? {
                      id: entry.id,
                      output: entry.toolCall.output,
                      isError: entry.toolCall.isError ?? false,
                    }
                  : null
              }
              expanded={expandedToolUse === entry.id}
              toggleExpanded={() =>
                setExpandedToolUse((was) =>
                  was === entry.id ? null : entry.id
                )
              }
              toolAfter={nextEntry?.entryType === "AssistantToolUse"}
              tool={toolsByName.get(entry.toolCall.toolName)}
              createdAt={entry.createdAt}
              completedAt={entry.toolCall.completedAt}
              onProvideResult={onProvideToolResult}
            />
          )}

        {/* UserAction entries are now grouped via userActionGroup */}
      </Message>
    </Fragment>
  );
}
